import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion"; // For animations
import {
  FaDownload,
  FaEnvelope,
  FaLinkedinIn,
  FaGithub,
  FaEye,
} from "react-icons/fa"; // Icons from react-icons
import { FaXTwitter } from "react-icons/fa6";
import profilePic from "../assets/images/Tirthoraj-profile-pic-3.jpg"; // Verified path

const roles = ["Developer", "Coder", "Student", "Gamer"];

// Google Drive CV configuration
const CV_CONFIG = {
  fileId: "1W0bYnh_XeDy0zlDfJCvGPVHzhbhEOBvS",
  // Using embed URL with zoom parameter for better display control
  viewUrl:
    "https://drive.google.com/file/d/1W0bYnh_XeDy0zlDfJCvGPVHzhbhEOBvS/preview?usp=embed_googleplus&zoom=125",
  downloadUrl:
    "https://drive.google.com/uc?export=download&id=1W0bYnh_XeDy0zlDfJCvGPVHzhbhEOBvS",
};

// Array of coding humor fun facts
const funFacts = [
  "Wrote a 'perfect' script, tested it, and nothing worked. Forgot to actually call the function!",
  "There are only 10 types of people in the world: those who understand binary and those who don't.",
  "99 little bugs in the code, 99 little bugs. Take one down, patch it around, 127 little bugs in the code!",
  "Why do programmers prefer dark mode? Because light attracts bugs! 🐛",
  "Programming is like sex: one mistake and you have to support it for the rest of your life.",
  "A SQL query goes into a bar, walks up to two tables and asks: 'Can I join you?'",
  "I would tell you a UDP joke, but you might not get it.",
  "Why do Java developers wear glasses? Because they can't C#!",
  "I'm not lazy, I'm just in energy-saving mode. Like my code optimization skills!",
  "Debugging: Being the detective in a crime movie where you are also the murderer.",
  "Code never lies, comments sometimes do. Trust issues with my own documentation! 📝",
];

function Home() {
  const homeRef = useRef(null);
  const [roleIndex, setRoleIndex] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentFunFact, setCurrentFunFact] = useState("");

  useEffect(() => {
    const currentRef = homeRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("animate-fade-in");
          setIsLoaded(true); // Trigger animations on scroll
        }
      },
      { threshold: 0.1 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    // Set random fun fact on component mount
    const randomFact = funFacts[Math.floor(Math.random() * funFacts.length)];
    setCurrentFunFact(randomFact);

    const interval = setInterval(() => {
      setRoleIndex((prevIndex) => (prevIndex + 1) % roles.length);
    }, 2000);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
      clearInterval(interval);
    };
  }, []);

  // Function to handle CV viewing - opens in new tab (not popup window)
  const handleViewCV = () => {
    window.open(CV_CONFIG.viewUrl, "_blank", "noopener,noreferrer");
  };

  // Function to handle CV download
  const handleDownloadCV = () => {
    // Create a temporary link element for download
    const link = document.createElement("a");
    link.href = CV_CONFIG.downloadUrl;
    link.download = "Tirthoraj_Bhattacharya_Resume.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Framer Motion variants for animations
  const textVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut", delay: 0.2 },
    },
  };

  const imageVariants = {
    hidden: { opacity: 0, x: -50, rotate: -5 },
    visible: {
      opacity: 1,
      x: 0,
      rotate: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  const roleVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut", delay: 0.4 },
    },
  };

  return (
    <section
      id="home"
      className="min-h-screen bg-gray-900 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden flex items-center justify-center"
      ref={homeRef}
    >
      {/* Subtle Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 opacity-95"></div>

      <div className="max-w-7xl mx-auto relative z-10 flex flex-col md:flex-row items-center md:items-start justify-center md:justify-between gap-12">
        {/* Left Column: Profile Picture & Buttons */}
        <motion.div
          className="w-full md:w-1/3 flex flex-col items-center md:items-start"
          variants={imageVariants}
          initial="hidden"
          animate={isLoaded ? "visible" : "hidden"}
        >
          <motion.img
            src={profilePic}
            alt="Tirthoraj Bhattacharya"
            className="rounded-full w-72 h-72 sm:w-80 sm:h-96 md:w-96 md:h-96 mt-20 object-cover border-2 border-white shadow-md mb-10 md:mb-10"
            whileHover={{
              scale: 1.05,
              opacity: 0.9,
              borderColor: "#52b788",
            }}
            transition={{ duration: 0.3 }}
          />

          {/* Fixed Button Layout - Better spacing and alignment */}
          <div className="w-full max-w-md flex flex-col space-y-4">
            {/* Top Row - CV Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              {/* CV View Button */}
              <motion.button
                onClick={handleViewCV}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex-1 flex items-center justify-center bg-emerald-500/20 text-emerald-400 px-5 py-3 rounded-xl hover:bg-emerald-500/30 transition-all duration-300 shadow-lg text-base font-medium border border-emerald-400/30 hover:border-emerald-400 hover:shadow-emerald-400/20 min-h-[48px]"
              >
                <FaEye className="mr-2 text-lg" />
                <span>View CV</span>
              </motion.button>

              {/* CV Download Button */}
              <motion.button
                onClick={handleDownloadCV}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex-1 flex items-center justify-center bg-blue-500/20 text-blue-400 px-5 py-3 rounded-xl hover:bg-blue-500/30 transition-all duration-300 shadow-lg text-base font-medium border border-blue-400/30 hover:border-blue-400 hover:shadow-blue-400/20 min-h-[48px]"
              >
                <FaDownload className="mr-2 text-lg" />
                <span>Download CV</span>
              </motion.button>
            </div>

            {/* Bottom Row - Contact Button */}
            <Link
              to="/contact"
              className="w-full flex items-center justify-center bg-purple-500/20 text-purple-400 px-5 py-3 rounded-xl hover:bg-purple-500/30 transition-all duration-300 shadow-lg text-base font-medium border border-purple-400/30 hover:border-purple-400 hover:shadow-purple-400/20 transform hover:scale-105 min-h-[48px]"
            >
              <FaEnvelope className="mr-2 text-lg" />
              <span>Get in Touch</span>
            </Link>
          </div>
        </motion.div>

        {/* Right Column: Text & Other Elements */}
        <motion.div
          className="w-full md:w-2/3 text-center md:text-left"
          variants={textVariants}
          initial="hidden"
          animate={isLoaded ? "visible" : "hidden"}
        >
          <h1 className="text-3xl mt-20 md:text-4xl font-bold mb-4 text-gray-100 font-['Inter']">
            Hello, I'm
          </h1>
          <h2 className="text-4xl md:text-6xl font-bold mb-5 text-emerald-400 font-['Inter'] transition-colors duration-300">
            Tirthoraj Bhattacharya
          </h2>
          <motion.h3
            className="text-3xl md:text-3xl text-orange-500 mb-8 font-['Inter'] font-semibold"
            variants={roleVariants}
            initial="hidden"
            animate={isLoaded ? "visible" : "hidden"}
            transition={{ delay: 0.4 }}
          >
            {roles[roleIndex]}
          </motion.h3>
          <p className="text-md md:text-lg text-gray-400 mb-10 font-['Inter'] font-normal leading-relaxed">
            <span className="font-semibold italic text-white">
              Building software, automating workflows, and crafting AI
              solutions.
            </span>
          </p>

          {/* Social Media Links */}
          <motion.div
            className="mt-6 flex justify-center md:justify-start gap-4"
            variants={textVariants}
            initial="hidden"
            animate={isLoaded ? "visible" : "hidden"}
            transition={{ delay: 0.8 }}
          >
            <a
              href="https://www.linkedin.com/in/tirthoraj-bhattacharya/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-emerald-500 transition transform hover:scale-110 text-3xl"
            >
              <FaLinkedinIn />
            </a>
            <a
              href="https://github.com/CodeTirtho97"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-emerald-500 transition transform hover:scale-110 text-3xl"
            >
              <FaGithub />
            </a>
            <a
              href="https://x.com/lucifer_7951"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-emerald-500 transition transform hover:scale-110 text-3xl"
            >
              <FaXTwitter />
            </a>
          </motion.div>

          {/* Call-to-Action Statistics (as Badges/Cards) */}
          <motion.div
            className="mt-6 mb-16 flex flex-wrap justify-center md:justify-start gap-4 font-serif"
            variants={textVariants}
            initial="hidden"
            animate={isLoaded ? "visible" : "hidden"}
            transition={{ delay: 0.6 }}
          >
            <span className="bg-gray-800/20 text-gray-100 px-4 py-2 rounded-full shadow-md hover:bg-gray-700/20 hover:text-emerald-400 transition hover:cursor-default">
              5+ Years of Coding
            </span>
            <span className="bg-gray-800/20 text-gray-100 px-4 py-2 rounded-full shadow-md hover:bg-gray-700/20 hover:text-emerald-400 transition hover:cursor-default">
              15+ Projects Completed
            </span>
            <span className="bg-gray-800/20 text-gray-100 px-4 py-2 rounded-full shadow-md hover:bg-gray-700/20 hover:text-emerald-400 transition hover:cursor-default">
              10K+ Lines of Code
            </span>
          </motion.div>

          {/* Fun Fact (Highlighted Div) - Now with random facts and matching bottom margin */}
          <motion.div
            className="font-serif border-t-4 border-emerald-400 bg-gray-600/20 text-gray-400 text-lg text-center px-4 py-4 rounded-lg shadow-md hover:text-emerald-300 transition hover:cursor-default mb-16"
            variants={textVariants}
            initial="hidden"
            animate={isLoaded ? "visible" : "hidden"}
            transition={{ delay: 0.6 }}
          >
            <span className="text-indigo-300 font-sans">Fun Fact:</span>{" "}
            {currentFunFact}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

export default Home;
